export type TFlipTransform = { horizontal: boolean; vertical: boolean }

type PixelCrop = {
	x: number
	y: number
	width: number
	height: number
}

const DEFAULT_FLIP: TFlipTransform = { horizontal: false, vertical: false }

const createImage = (file: File): Promise<HTMLImageElement> => {
	return new Promise((resolve, reject) => {
		const image = new Image()

		image.crossOrigin = 'anonymous'
		image.addEventListener('load', () => {
			window.URL.revokeObjectURL(image.src)
			resolve(image)
		})
		image.addEventListener('error', (error) => {
			reject(error)
		})

		if (file.type.includes('image/svg')) {
			const reader = new FileReader()
			reader.onload = (e) => {
				image.src = e?.target?.result as string
			}
			reader.readAsDataURL(file)
		} else {
			image.src = window.URL.createObjectURL(file)
		}
	})
}

export const getRadianAngle = (degreeValue: number): number => {
	return (degreeValue * Math.PI) / 180
}

export const rotateSize = (
	width: number,
	height: number,
	rotation: number,
): { width: number; height: number } => {
	const rotRad = getRadianAngle(rotation)

	return {
		width: Math.floor(
			Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
		),
		height: Math.floor(
			Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
		),
	}
}

export const exportFileFromCanvas = (
	imageSrc: File,
	canvas: HTMLCanvasElement,
): Promise<File> => {
	return new Promise((resolve) => {
		const type = imageSrc.type.includes('svg') ? 'image/png' : imageSrc.type
		canvas.toBlob((file) => {
			if (file) {
				resolve(new File([file], imageSrc.name, { type }))
				return
			}
			resolve(imageSrc)
		}, type)
	})
}

export const getCroppedImg = async (
	imageSrc: File,
	pixelCrop: PixelCrop = { x: 10, y: 10, width: 50, height: 50 },
	rotation = 0,
	flip = DEFAULT_FLIP,
): Promise<File> => {
	const image = await createImage(imageSrc)
	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')

	if (!ctx) {
		return imageSrc
	}

	const rotRad = getRadianAngle(rotation)

	// calculate bounding box of the rotated image
	const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
		image.width,
		image.height,
		rotation,
	)

	// set canvas size to match the bounding box
	canvas.width = bBoxWidth
	canvas.height = bBoxHeight

	// translate canvas context to a central location to allow rotating and flipping around the center
	ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
	ctx.rotate(rotRad)
	ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1)
	ctx.translate(-image.width / 2, -image.height / 2)

	// draw rotated image
	ctx.drawImage(image, 0, 0)

	// croppedAreaPixels values are bounding box relative
	// extract the cropped image using these values
	const data = ctx.getImageData(
		pixelCrop.x,
		pixelCrop.y,
		pixelCrop.width,
		pixelCrop.height,
	)

	// set canvas width to final desired crop size - this will clear existing context
	canvas.width = pixelCrop.width
	canvas.height = pixelCrop.height

	// paste generated rotate image at the top left corner
	ctx.putImageData(data, 0, 0)

	// As Base64 string
	// return canvas.toDataURL('image/jpeg');

	// As a blob
	return exportFileFromCanvas(imageSrc, canvas)
}
